import React from 'react';
import { useNavigate } from 'react-router';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { OrderType } from '@lds-efsp/shared-contracts';
import { withFreshStartSignal } from '../../efiling/lib/resolve-entry-intent';

interface OrderTypeMeta {
  type: OrderType;
  label: string;
  description: string;
  icon: string;
}

const ORDER_TYPES: OrderTypeMeta[] = [
  {
    type: 'e-filing',
    label: 'eFiling',
    description: 'File documents electronically with California courts',
    icon: '\u26A1',
  },
  {
    type: 'court-filing',
    label: 'Court Filing',
    description: 'Physical filing at California courts',
    icon: '\uD83C\uDFDB\uFE0F',
  },
  {
    type: 'process-serving',
    label: 'Process Serving',
    description: 'Serve legal documents to parties',
    icon: '\uD83D\uDCE8',
  },
  {
    type: 'document-retrieval',
    label: 'Document Retrieval',
    description: 'Retrieve copies of filed documents',
    icon: '\uD83D\uDD0D',
  },
  {
    type: 'courtesy-copy-delivery',
    label: 'Courtesy Copy Delivery',
    description: 'Deliver courtesy copies to judges or departments',
    icon: '\uD83D\uDCCB',
  },
  {
    type: 'secretary-of-state-filing',
    label: 'Secretary of State Filing',
    description: 'File documents with the Secretary of State',
    icon: '\uD83C\uDFE2',
  },
  {
    type: 'county-recording',
    label: 'County Recording',
    description: 'Record documents with the county recorder',
    icon: '\uD83D\uDCC4',
  },
  {
    type: 'skip-trace',
    label: 'Skip Trace',
    description: 'Locate individuals for service of process',
    icon: '\uD83D\uDD75\uFE0F',
  },
  {
    type: 'eservice',
    label: 'eService',
    description: 'Electronic service of documents to parties',
    icon: '\uD83D\uDCE7',
  },
];

interface OrderTypeSelectorProps {
  brand: BrandConfig;
}

const OrderTypeSelector: React.FC<OrderTypeSelectorProps> = ({ brand }) => {
  const navigate = useNavigate();

  const handleSelect = (type: OrderType) => {
    if (type === 'e-filing') {
      // Fresh-start launch site: carry the fresh-start signal (and no ?orderId=)
      // so the wizard resolves on entry as a fresh start (R6).
      navigate(withFreshStartSignal('/eFiling'));
    } else {
      navigate(`/orders/new/${type}`);
    }
  };

  return (
    <div>
      <h2
        className="text-lg font-semibold mb-4"
        style={{ color: brand.colors.text }}
      >
        Select Order Type
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ORDER_TYPES.map((meta) => (
          <button
            key={meta.type}
            onClick={() => handleSelect(meta.type)}
            className="text-left p-4 border rounded-lg hover:shadow-md transition-shadow"
            style={{
              borderColor: brand.colors.border,
              backgroundColor: brand.colors.bgWhite || '#FFFFFF',
            }}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{meta.icon}</span>
              <div>
                <h3
                  className="font-medium text-sm"
                  style={{ color: brand.colors.text }}
                >
                  {meta.label}
                </h3>
                <p
                  className="text-xs mt-1"
                  style={{ color: brand.colors.textLight }}
                >
                  {meta.description}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default OrderTypeSelector;
